import React from "react";
import { Box, Typography, Tooltip } from "@mui/material";
import {
  Videocam as VideocamIcon,
  Mic as MicIcon,
  GraphicEq as GraphicEqIcon,
} from "@mui/icons-material";
import {
  fieldLabelStyles,
  modeToggleRowStyles,
  getModeToggleButtonStyles,
} from "./styles";

export type RoleplayMode = "audio" | "video" | "async";

interface RoleplayModeToggleProps {
  canStart?: boolean;
  videoDisabled?: boolean;
  videoDisabledReason?: string;
  submittingMode?: RoleplayMode | null;
  onSelect: (mode: RoleplayMode) => void;
}

export const RoleplayModeToggle: React.FC<RoleplayModeToggleProps> = ({
  canStart = false,
  videoDisabled = false,
  videoDisabledReason,
  submittingMode = null,
  onSelect,
}) => {
  const renderButton = (
    mode: RoleplayMode,
    icon: React.ReactNode,
    label: string,
    tooltip?: string,
  ) => {
    const isSubmitting = submittingMode === mode;
    const isVideo = mode === "video";
    const isDisabled = !canStart || isSubmitting || (isVideo && videoDisabled);
    const tip =
      isVideo && videoDisabled && videoDisabledReason
        ? videoDisabledReason
        : tooltip;

    const button = (
      <Box
        component="button"
        type="button"
        disabled={isDisabled}
        onClick={() => !isDisabled && onSelect(mode)}
        sx={getModeToggleButtonStyles(isDisabled)}
      >
        {icon}
        <Typography
          component="span"
          sx={{
            fontSize: "12px",
            fontWeight: 500,
            textAlign: "center",
            whiteSpace: "nowrap",
            lineHeight: 1.2,
          }}
        >
          {isSubmitting ? "Wait..." : label}
        </Typography>
      </Box>
    );

    if (tip) {
      return (
        <Tooltip key={mode} title={tip} arrow>
          <Box sx={{ flex: 1, minWidth: 0 }}>{button}</Box>
        </Tooltip>
      );
    }

    return (
      <Box key={mode} sx={{ flex: 1, minWidth: 0 }}>
        {button}
      </Box>
    );
  };

  return (
    <Box sx={{ width: "100%" }}>
      <Typography sx={fieldLabelStyles}>Mode</Typography>
      <Box sx={modeToggleRowStyles}>
        {renderButton(
          "audio",
          <MicIcon className="mode-toggle-icon" sx={{ fontSize: 18 }} />,
          "Audio",
        )}
        {renderButton(
          "video",
          <VideocamIcon className="mode-toggle-icon" sx={{ fontSize: 18 }} />,
          "Video",
        )}
        {renderButton(
          "async",
          <GraphicEqIcon className="mode-toggle-icon" sx={{ fontSize: 18 }} />,
          "Voice Messaging",
        )}
      </Box>
    </Box>
  );
};
